import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset as authConfirmPasswordReset,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as authSignOut,
  TwitterAuthProvider,
  updateEmail as authUpdateEmail,
  updatePassword as authUpdatePassword,
  updateProfile as authUpdateProfile,
  sendPasswordResetEmail as authSendPasswordResetEmail
} from 'firebase/auth';
import firebaseApp from './firebase';
import { createUser, updateUser, useGetPersonnel, useUser, useGetPermissionData } from './db';
import { browserHistory } from './router';
import PageLoader from '../components/PageLoader';
import CSVReader from '../components/CSVParser/CSVParser';

// Whether to merge extra user data from database into `auth.user`
const MERGE_DB_USER = true;
// Whether to send email verification on signup
const EMAIL_VERIFICATION = true;
// Whether to connect analytics session to `user.uid`
const ANALYTICS_IDENTIFY = true;

// Initialize Firebase auth
const fireBaseAuth = getAuth(firebaseApp);

// Track Firebase initialization state
let firebaseInitialized = false;
let firebaseInitPromise = null;

// Ensure Firebase is fully initialized before allowing operations
const ensureFirebaseReady = () => {
  if (firebaseInitialized) {
    return Promise.resolve();
  }

  if (!firebaseInitPromise) {
    firebaseInitPromise = new Promise((resolve) => {
      // Wait for Firebase auth to be ready
      const unsubscribe = onAuthStateChanged(fireBaseAuth, () => {
        firebaseInitialized = true;
        unsubscribe();
        resolve();
      });

      // Timeout fallback to prevent indefinite waiting
      setTimeout(() => {
        firebaseInitialized = true;
        unsubscribe();
        resolve();
      }, 3000);
    });
  }

  return firebaseInitPromise;
};

// Create a `useAuth` hook and `AuthProvider` that enables
// any component to subscribe to auth and re-render when it changes.
const authContext = createContext();
export const useAuth = () => useContext(authContext);

function useMergeExtraData(user, { enabled }) {
  // Get extra user data from database
  const { data, status, error } = useUser(enabled && user && user.uid);
  // Memoize so returned object has a stable identity
  return useMemo(() => {
    // If disabled or no auth user (yet) then just return
    if (!enabled || !user) return user;

    switch (status) {
      case 'success':
        // If successful, but `data` is `null`, that means user just signed up and the `createUser`
        // function hasn't populated the db yet. Return `null` to indicate auth is still loading.
        // The above call to `useUser` will re-render things once the data comes in.
        if (data === null) return null;
        // Return auth `user` merged with extra user `data`
        return { ...user, ...data };
      case 'error':
        // Uh oh.. Let's at least show a helpful error.
        throw new Error(`
          Error: ${error.message}
          This happened while attempting to fetch extra user data from the database
          to include with the authenticated user. Make sure the database is setup or
          disable merging extra user data by setting MERGE_DB_USER to false.
        `);
      default:
        // We have an `idle` or `loading` status so return `null`
        // to indicate that auth is still loading.
        return null;
    }
  }, [user, enabled, data, status, error]);
}

const authProviders = [
  {
    id: 'password',
    name: 'password'
  },
  {
    id: 'google.com',
    name: 'google',
    get: () => new GoogleAuthProvider()
  },
  {
    id: 'facebook.com',
    name: 'facebook',
    get: () => {
      const provider = new FacebookAuthProvider();
      provider.setCustomParameters({ display: 'popup' });
      return provider;
    }
  },
  {
    id: 'twitter.com',
    name: 'twitter',
    get: () => new TwitterAuthProvider()
  },
  {
    id: 'github.com',
    name: 'github',
    get: () => new GithubAuthProvider()
  },
  {
    id: 'microsoft.com',
    name: 'microsoft',
    get: () => new OAuthProvider('microsoft.com')
  }
];

// Wait for Firebase user to be initialized before resolving promise
// and taking any further action (such as writing to the database)
const waitForFirebase = (uid) => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(fireBaseAuth, (user) => {
      // Ensure we have a user with expected `uid`
      if (user && user.uid === uid) {
        resolve(user); // Resolve promise
        unsubscribe(); // Prevent from firing again
      }
    });
  });
};

const getFromQueryString = (key) => {
  return queryString.parse(window.location.search)[key];
};

function useFormatUser(user) {
  // Memoize so returned object has a stable identity
  return useMemo(() => {
    // Return if auth user is `null` (loading) or `false` (not authenticated)
    if (!user) return user;

    // Create an array of user's auth providers by id (["password", "google", etc])
    // Components can read this to prompt user to re-auth with the correct provider
    const providers = user.providerData.map(({ providerId }) => {
      return authProviders.find((p) => p.id === providerId)?.name;
    });

    return {
      // Include full auth user data
      ...user,
      // Alter the names of some fields
      name: user.displayName,
      picture: user.photoURL,
      // User's auth providers
      providers: providers || []
    };
  }, [user]);
}

// Hook that creates the `auth` object and handles state
// This is called from `AuthProvider` above (extracted out for readability)
function useAuthProvider() {
  // Store auth user in state
  // `user` will be object, `null` (loading) or `false` (logged out)
  const { data: personnel } = useGetPersonnel();
  const [user, setUser] = useState(null);
  const [currentPersonnel, setCurrentPersonnel] = useState([]);
  // permission table
  const [permissionData, setPermissionData] = useState(null);
  const [permissionsReady, setPermissionsReady] = useState(false);

  // personalSession
  const [personalSession, setPersonalSession] = useState(null);

  useEffect(() => {
    if (user) {
      (async () => {
        try {
          const result = await useGetPermissionData();
          setPermissionData(result);
          setPermissionsReady(true);
        } catch (error) {
          console.error('Error loading permission data:', error);
          // Set permissions ready even on error to avoid blocking the UI
          setPermissionsReady(true);
        }
      })();
    }
  }, [user]);

  useEffect(() => {
    if (Array.isArray(personnel) && personnel.length > 0) {
      setCurrentPersonnel(personnel);
    }
  }, [personnel]);

  useEffect(() => {
    if (currentPersonnel.length > 0 && user) {
      const matchingUser = currentPersonnel.find((person) => person.email === user.email);
      if (matchingUser) {
        setPersonalSession(matchingUser);
      }
    }
  }, [currentPersonnel, user]);

  // Merge extra user data from the database
  // This means extra user data (such as payment plan) is available as part
  // of `auth.user` and doesn't need to be fetched separately. Convenient!
  let finalUser = useMergeExtraData(user, { enabled: MERGE_DB_USER });

  // Add custom fields and formatting to the `user` object
  finalUser = useFormatUser(finalUser);

  // Handle response from auth functions (`signup`, `signin`, and `signinWithProvider`)
  const handleAuth = async (response) => {
    try {
      const { user: returnedUser } = response;
      const { isNewUser } = getAdditionalUserInfo(response);

      // Ensure Firebase user is ready before we continue
      await waitForFirebase(returnedUser.uid);

      // Create the user in the database if they are new
      if (isNewUser) {
        await createUser(returnedUser.uid, { email: returnedUser.email });
        // Send email verification if enabled
        if (EMAIL_VERIFICATION) {
          sendEmailVerification(fireBaseAuth.currentUser);
        }
      }
      const matchingUser = currentPersonnel.find((p) => p.email === returnedUser.email);
      if (matchingUser) {
        returnedUser.permission = matchingUser.permission;
      }
      // Update user in state
      setUser(returnedUser);

      return returnedUser;
    } catch (error) {
      console.error('Error in handleAuth:', error);
      // Re-throw the error so calling code can handle it
      throw error;
    }
  };

  const signup = (email, password) => {
    return createUserWithEmailAndPassword(fireBaseAuth, email, password).then(handleAuth);
  };

  const signin = (email, password) => {
    return signInWithEmailAndPassword(fireBaseAuth, email, password).then(handleAuth);
  };

  const signinWithProvider = async (name) => {
    // Ensure Firebase is ready before attempting sign-in
    await ensureFirebaseReady();

    // Get provider by name ("google", "facebook", etc)
    const provider = authProviders.find((p) => p.name === name).get();
    return signInWithPopup(fireBaseAuth, provider).then(handleAuth);
  };

  const signout = () => {
    return authSignOut(fireBaseAuth);
  };

  const sendPasswordResetEmail = (email) => {
    return authSendPasswordResetEmail(fireBaseAuth, email);
  };

  const confirmPasswordReset = (password, code) => {
    // Get code from query string object
    const resetCode = code || getFromQueryString('oobCode');
    return authConfirmPasswordReset(fireBaseAuth, resetCode, password);
  };

  const updatePassword = (password) => {
    return authUpdatePassword(fireBaseAuth.currentUser, password);
  };

  // Update auth user and persist data to database
  // Call this function instead of multiple auth/db update functions
  const updateProfile = async (data) => {
    const { email, name, picture } = data;

    // Update auth email
    if (email) {
      await authUpdateEmail(fireBaseAuth.currentUser, email);
    }

    // Update built-in auth profile fields
    // These fields are renamed in `useFormatUser`, so when updating we
    // need to make sure to use their original names (`displayName`, `photoURL`, etc)
    if (name || picture) {
      const fields = {};
      if (name) fields.displayName = name;
      if (picture) fields.photoURL = picture;
      await authUpdateProfile(fireBaseAuth.currentUser, fields);
    }

    // Persist all data to the database
    await updateUser(user.uid, data);

    // Update user in state
    setUser(fireBaseAuth.currentUser);
  };

  const renderAddToFileWithPermissions = (
    handleResults,
    auth,
    updatedPersonnel,
    loading,
    title
  ) => {
    const matchingUser = updatedPersonnel.find(
      (p) => p.email.toLowerCase() === auth.user.email.toLowerCase()
    );
    if (matchingUser) {
      if (matchingUser.permission === 'Read-only') {
        return <></>;
      }
    }
    return (
      <CSVReader
        config={{ encoding: 'ISO-8859-1' }}
        handleResults={handleResults}
        loading={loading}
        title={title}
      />
    );
  };

  useEffect(() => {
    // Subscribe to user on mount
    const unsubscribe = onAuthStateChanged(fireBaseAuth, (u) => {
      if (u) {
        setUser(u);
      } else {
        setUser(false);
      }
    });

    // Unsubscribe on cleanup
    return () => unsubscribe();
  }, []);

  return {
    user: finalUser,
    permissionData,
    personalSession,
    signup,
    signin,
    signinWithProvider,
    signout,
    sendPasswordResetEmail,
    confirmPasswordReset,
    updatePassword,
    updateProfile,
    renderAddToFileWithPermissions,
    permissionsReady
  };
}

// This should wrap the app in `src/pages/_app.js`
export function AuthProvider({ children }) {
  const authProvider = useAuthProvider();
  return <authContext.Provider value={authProvider}>{children}</authContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// A Higher Order Component for requiring authentication
export const requireAuth = (Component) => {
  return (props) => {
    // Get authenticated user
    const auth = useAuth();

    useEffect(() => {
      // Redirect if not signed in
      if (auth.user === false) {
        browserHistory.replace('/auth/signin');
      }
    }, [auth]);

    // Show loading indicator
    // We're either loading (user is `null`) or about to redirect from above `useEffect` (user is `false`)
    if (!auth.user) {
      return <PageLoader />;
    }

    // Render component now that we have user
    return <Component {...props} />;
  };
};

// Handle Firebase email link for reverting to original email after an email change
export const handleRecoverEmail = async (code) => {
  // Check that action code is valid
  const info = await checkActionCode(fireBaseAuth, code);
  // Revert to original email by applying action code
  await applyActionCode(fireBaseAuth, code);
  // Send password reset email so user can change their password in the case
  // that someone else got into their account and changed their email.
  await authSendPasswordResetEmail(fireBaseAuth, info.data.email);
  // Return original email so it can be displayed by calling component
  return info.data.email;
};

// Handle Firebase email link for verifying email
export const handleVerifyEmail = (code) => {
  return applyActionCode(fireBaseAuth, code);
};
